/** *************************************************************
 * Copyright (c) 2006 - 2021, Case Anywhere, LLC.
 * All rights reserved
 *
 * @author Brad Wetmore
 * Date: 10/07/2019
 *
 * @version 0.2
 * Revisions:
 * 10/07/2019 - BW - Initial Creation
 * 07/22/2021 - BW - Modified: _onSendEmailClick - adjusted error catch to
 *                   use:"processErrorWithModalFailAlertWithNotifiedMessage" instead
 *
 * Description/Purpose:
 * Forgot Credential JS
 ************************************************************** */
import WindowModal from "component/modal/window-modal";
import Modal from "component/modal/modal";
import ValidateForm from "util/validate-form";
import ButtonUtil from "util/button-util";
import StackTraceError from "component/stack-trace-error/stack-trace-error";
import ModalUtil from "util/modal-util";
import Banner from "component/banner/banner";
import { PAGE_PARAM } from "constant/app-constant";
import AjaxUtil from "util/ajax-util";


export default class ForgotCredential {
  static get typeUsername() {
    return 'username';
  }

  static get typePassword() {
    return 'password';
  }

  constructor($event) {
    try {
      this._$event = $event;
      this._$elem = $(this._$event.target);
      this._init();
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Constructor');
    }
  }


  /**
     * Init method - called from the constructor
     * @private
     */
  _init() {
    this._cacheDom();
  }


  /**
     * Caches some often used DOM objects
     * @returns {ForgotCredential}
     * @private
     */
  _cacheDom() {
    try {
      // Only modal-template stuff
      this._$formModalTemplate = $(`#forgot_credential_form${Modal.modalSuffix}`);
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Cache Dom');
    }
  }


  /**
     * Caches modal elements
     * @returns {ForgotCredential}
     * @private
     */
  _modalCacheDom() {
    try {
      this._$form = $('#forgot_credential_form');
      this._$input = $('#forgot_credential_input');
      this._$submitButton = $('#forgot_credential_submit_button');
      this._$label = $('#forgot_credential_label');
      this._$instruction = $('#forgot_credential_instruction');
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Modal Cache Dom');
    }
  }


  /**
     * Set up some common variables
     * @private
     */
  _setUpVariable() {
    try {
      this.opts = {
        username: {
          title: 'Forgot Username',
          label: 'Email Address',
          instruction: `Please enter your email address. &nbsp;
                      An email will be sent to the address registered with your account.`,
          requiredMessage: 'Please fill in an Email Address.',
          successMessage: 'If you have entered a valid email address, your username will be sent shortly.',
        },
        password: {
          title: 'Forgot Password',
          label: 'Username',
          instruction: `Please enter your username. &nbsp;
                      An email with a link to reset your password will be sent to the address registered
                      with your account.`,
          requiredMessage: 'Please fill in a Username.',
          successMessage: 'If you have entered a valid username, your password will be sent shortly.',
        },
      };
      this._type = this.opts[this._$elem.data('type')];
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Set Up Variable');
    }
  }


  /**
     * Register the ValidateForm Event and setup the object
     * @private
     */
  _registerValidateForm() {
    try {
      this.validateForm = new ValidateForm($('#forgot_credential_form'),
        {
          errorBanner: '#forgot_credential_error_banner',
          errorBannerList: '#forgot_credential_error_banner_list',
          addErrorBannerParent: this._$form,
        })
        .addRules({
          forgot_credential_input: {
            required: true,
            email: false,
          },
        })
        .addMessages({
          forgot_credential_input: {
            required: `${this._type.requiredMessage}`,
          },
        })
        .addSubmitHandler(this._onSendEmailClick.bind(this))
        .init();
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Register Validation Form');
    }
  }


  /**
     * Render/SetUp Modal
     * @private
     */
  _renderModal() {
    try {
      this._$label.empty().text(this._type.label);
      this._$instruction.html(this._type.instruction);
      const eAct = this._$elem.data('eact');
      this._$form.prop('action', `${this._$form.prop('action')}?${PAGE_PARAM.E_ACT}=${eAct}`);
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Render Modal');
    }
  }


  /**
     * Submit Handler
     * @param $event
     * @private
     */
  _onSendEmailClick($event) {
    try {
      $event.preventDefault();
      ButtonUtil.enableProcessingButton(this._$submitButton, false);

      // Make ajax save call
      const request = AjaxUtil.postJson(this._$form);
      request.done((data) => {
        ButtonUtil.disableProcessingButton(this._$submitButton, false, true);
        new Banner({
          isSuccess: true,
          header: `${this._type.successMessage}`,
          onlyHeader: true,
          banner: '#forgot_credential_success_banner',
          bannerParent: this._$form,
          show: true,
        })
          .render();
      });
      request.fail((jqXHR, textStatus, errorThrown) => {
        StackTraceError.processErrorWithModalFailAlertWithNotifiedMessage(
          new Error(errorThrown), 'Forgot Credential Send Email Click Handler',
        );
        // ModalUtil.showModalFailAlert();
        // StackTraceError.processError(new Error(errorThrown),
        // 'Forgot Credential Send Email Click Handler (UI Alert Displayed)');
      });
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Send Email Click');
    }
  }


  /**
     * Will open the Forgot Credential modal window
     */
  _openModal() {
    try {
      // Update some Forgot Credential modal data
      this.windowModal = new WindowModal({
        closeMethods: ['overlay'],
        cssClass: ['forgot-credential-modal'],
      })
        .addHeader(this._type.title, true)
        .addCustomIcon()
        .setContent(this._$formModalTemplate.prop('outerHTML'))
        .addOnOpenFocusToElement($('#forgot_credential_input')) // don't use cacheDOM
        .open();
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Open Modal');
    }
  }


  /**
     * Will open the Forgot Credential modal window
     * We then call the registerValidateForm after the modal is open and the elements have been init
     */
  open() {
    try {
      this._setUpVariable();
      this._openModal();
      this._modalCacheDom();
      this._renderModal();
      this._registerValidateForm();
    } catch (e) {
      StackTraceError.processError(e, 'Forgot Credential - Open');
    }
  }
}
